import type {
  ConnectorConnection,
  ConnectorProject,
  ConnectorProvider,
} from '@saaga/shared/interfaces/connector.interface'
import {
  useGetConnectorConnectionsQuery,
  useGetConnectorProvidersQuery,
} from '@saaga/shared/store/connector-endpoints.store'
import { useGetConnectorProjectsQuery } from '@saaga/shared/store/connector-project-endpoints.store'
import { useMemo } from 'react'

import type { UseConnectorHandlersReturn } from './use-connector-handlers.hook'
import { useConnectorHandlers } from './use-connector-handlers.hook'

export interface UseConnectorDetailReturn extends UseConnectorHandlersReturn {
  provider: ConnectorProvider | null
  connection: ConnectorConnection | null
  projectList: ConnectorProject[]
  isLoading: boolean
}

export const useConnectorDetail = (providerSlug: string): UseConnectorDetailReturn => {
  const { data: providerList = [], isLoading: isProvidersLoading } = useGetConnectorProvidersQuery()
  const { data: connectionsData, isLoading: isConnectionsLoading } =
    useGetConnectorConnectionsQuery()
  const connectionList = connectionsData?.itemList ?? []

  const provider = useMemo(
    () => providerList.find((p) => p.slug === providerSlug) ?? null,
    [providerList, providerSlug]
  )

  const connection = useMemo(
    () => connectionList.find((c) => c.provider === providerSlug && c.status === 'active') ?? null,
    [connectionList, providerSlug]
  )

  const { data: projectsData, isLoading: isProjectsLoading } = useGetConnectorProjectsQuery(
    connection?.id ?? '',
    { skip: !connection }
  )
  const projectList = projectsData?.itemList ?? []

  const handlers = useConnectorHandlers(connectionList)

  return {
    provider,
    connection,
    projectList,
    isLoading: isProvidersLoading || isConnectionsLoading || isProjectsLoading,
    ...handlers,
  }
}
